import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  numericToNumber,
  APIError,
  type BulkDecisionsRequest,
  type ChannelScore,
  type CreateDecisionRequest,
  type EvaluationTask,
} from "@/lib/api";
import {
  fmtNumber,
  fmtPercent,
  fmtRank,
  fmtRelativeDays,
  fmtTimestamp,
  platformLabel,
} from "@/lib/format";
import { PlatformBadge } from "@/components/PlatformBadge";
import { cn } from "@/lib/utils";
import {
  Badge,
  lifecycleVariant,
  lifecycleLabel,
  taskStatusVariant,
  taskStatusLabel,
  decisionVariant,
  priorityVariant,
} from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";

// ---------------------------------------------------------------------------
// Evaluation Hub — the core inbox per SPEC §8.4.
//
// Layout: filter bar (top), task list (left), detail + decision panel (right).
// Decision actions POST /api/evaluations → triggers lifecycle transition + audit.
// ---------------------------------------------------------------------------

const TASK_STATUSES = [
  { value: "", label: "全部状态" },
  { value: "pending", label: "pending" },
  { value: "fetching", label: "fetching" },
  { value: "scored", label: "scored (待决策)" },
  { value: "decided", label: "decided" },
  { value: "failed", label: "failed" },
];

// Task priority sources (column name: evaluation_tasks.priority):
//   high   — competitor 推广 trigger（KOL 突然给竞品做了推广，第一时间评估）
//   medium — CSV 批量导入 / 手动添加 / 手动重评 / 调度器周期重评（默认）
//   low    — keyword_discovery 自动发现 (v2.3.5) / 长期监控
const PRIORITIES = [
  { value: "", label: "全部优先级" },
  { value: "high", label: "high (竞品触发)" },
  { value: "medium", label: "medium (常规)" },
  { value: "low", label: "low (关键词发现)" },
];

// v2.3.5 — task source filter. evaluation_tasks.source tracks which entry
// point created the task. Keyword-discovered tasks default to priority=low
// and are kept in their own stream so they don't drown out CSV/manual work.
const SOURCES = [
  { value: "", label: "全部来源" },
  { value: "csv_import", label: "CSV 导入" },
  { value: "manual", label: "频道库重评" },
  { value: "manual_add", label: "手动添加" },
  { value: "re_evaluation", label: "调度器回收" },
  { value: "keyword_discovery", label: "关键词发现" },
];

type DecisionKind = "candidate" | "monitor_only" | "rejected" | "deferred";

const PAGE_SIZE = 50;

export default function EvaluationHub() {
  const [status, setStatus] = useState("scored");
  const [priority, setPriority] = useState("");
  // v2.3.5 — source filter so the keyword-discovery feed can be triaged
  // separately from CSV / manual / cron-recycled tasks.
  const [source, setSource] = useState("");
  // v2.3.3 — pagination. Was missing, so historical decided/rejected tasks
  // past the first 50 couldn't be reached.
  const [page, setPage] = useState(1);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  // v2.2.5 — bulk decision multi-select. `checkedIds` is the set of
  // task_ids ticked; non-empty triggers the sticky bulk action bar.
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [pendingBulk, setPendingBulk] = useState<DecisionKind | null>(null);

  const tasksQ = useQuery({
    queryKey: ["evaluation-tasks", { status, priority, source, page }],
    queryFn: () =>
      endpoints.evaluationTasks({ status, priority, source, page, limit: PAGE_SIZE }),
  });

  // When the list reloads, auto-select the first task if nothing is selected
  // (or if the selected one fell off the list).
  const tasks = tasksQ.data?.data ?? [];
  const hasNext = tasksQ.data?.next_page !== undefined;
  const selectedTask = useMemo(
    () => tasks.find((t) => t.ID === selectedTaskId) ?? tasks[0],
    [tasks, selectedTaskId],
  );

  // Prune the checked set when filters change so stale ids don't sneak
  // into a bulk POST (they'd 404 anyway, but partial-fail is uglier).
  const visibleIds = useMemo(() => new Set(tasks.map((t) => t.ID)), [tasks]);
  const validCheckedIds = useMemo(
    () => Array.from(checkedIds).filter((id) => visibleIds.has(id)),
    [checkedIds, visibleIds],
  );
  const allVisibleChecked =
    tasks.length > 0 && tasks.every((t) => checkedIds.has(t.ID));

  const toggleCheck = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleCheckAll = () => {
    if (allVisibleChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(tasks.map((t) => t.ID)));
    }
  };
  const clearChecked = () => setCheckedIds(new Set());

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">评估中心</h1>
          <span className="text-sm text-muted-foreground">
            第 {page} 页 · 本页 {tasks.length} 条
          </span>
          <div className="ml-auto flex gap-2">
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              {TASK_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
            <Select
              value={priority}
              onChange={(e) => {
                setPriority(e.target.value);
                setPage(1);
              }}
            >
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
            <Select
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setPage(1);
              }}
            >
              {SOURCES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1 || tasksQ.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              上一页
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext || tasksQ.isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-80 shrink-0 overflow-y-auto border-r border-border bg-background">
          {tasksQ.isLoading && (
            <p className="p-4 text-sm text-muted-foreground">加载中…</p>
          )}
          {tasksQ.isError && (
            <p className="p-4 text-sm text-destructive">
              加载失败：{(tasksQ.error as Error).message}
            </p>
          )}
          {tasksQ.data && tasks.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground space-y-2">
              <p>没有匹配的评估任务</p>
              {status === "scored" && (
                <p className="text-xs">
                  当前过滤为 <code>scored</code>；若怀疑有任务卡在前置阶段，
                  切换到 <code>pending</code> 或 <code>fetching</code> 查看队列。
                </p>
              )}
            </div>
          )}
          {tasks.length > 0 && (
            <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background px-3 py-2 text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allVisibleChecked}
                  onChange={toggleCheckAll}
                  className="h-3.5 w-3.5"
                />
                <span className="text-muted-foreground">全选</span>
              </label>
              {checkedIds.size > 0 && (
                <span className="text-muted-foreground">
                  已选 {checkedIds.size}
                </span>
              )}
              {checkedIds.size > 0 && (
                <button
                  onClick={clearChecked}
                  className="ml-auto text-muted-foreground hover:text-foreground"
                >
                  清除
                </button>
              )}
            </div>
          )}
          <ul>
            {tasks.map((t) => (
              <TaskListItem
                key={t.ID}
                task={t}
                active={(selectedTask?.ID ?? -1) === t.ID}
                onClick={() => setSelectedTaskId(t.ID)}
                checked={checkedIds.has(t.ID)}
                onCheck={() => toggleCheck(t.ID)}
              />
            ))}
          </ul>
        </aside>

        <section className="flex-1 overflow-y-auto p-6">
          {checkedIds.size > 0 ? (
            <BulkActionPanel
              taskIds={validCheckedIds}
              pendingDecision={pendingBulk}
              setPendingDecision={setPendingBulk}
              onDone={() => {
                clearChecked();
                tasksQ.refetch();
                setPendingBulk(null);
              }}
            />
          ) : selectedTask ? (
            <TaskDetailPanel
              key={selectedTask.ID}
              task={selectedTask}
              onDecided={() => {
                // Refresh list; selection naturally falls to the next.
                tasksQ.refetch();
                setSelectedTaskId(null);
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              在左侧选择一个评估任务,或勾选多个进行批量决策
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

// BulkActionPanel — v2.2.5. Replaces the detail pane when ≥1 task is
// checked. Operator picks one of 4 decisions; the panel then shows a
// confirmation form (with optional shared rationale + next_review_at
// for deferred), runs the bulk POST, and renders a partial-success
// summary.
function BulkActionPanel({
  taskIds,
  pendingDecision,
  setPendingDecision,
  onDone,
}: {
  taskIds: number[];
  pendingDecision: DecisionKind | null;
  setPendingDecision: (d: DecisionKind | null) => void;
  onDone: () => void;
}) {
  const [rationale, setRationale] = useState("");
  const [nextReview, setNextReview] = useState("");

  const mut = useMutation({
    mutationFn: (body: BulkDecisionsRequest) => endpoints.bulkCreateDecisions(body),
  });

  const submit = () => {
    if (!pendingDecision) return;
    const body: BulkDecisionsRequest = {
      task_ids: taskIds,
      decision: pendingDecision,
      rationale: rationale.trim() || undefined,
    };
    if (pendingDecision === "deferred") {
      if (!nextReview) return;
      body.next_review_at = new Date(nextReview).toISOString();
    }
    mut.mutate(body);
  };

  // Done state — render summary + "完成" button to clear.
  if (mut.isSuccess && mut.data) {
    const failed = mut.data.results.filter((r) => !r.success);
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">批量决策结果</h2>
        <div className="flex gap-4 text-sm">
          <span className="rounded bg-emerald-100 px-3 py-1.5 text-emerald-800">
            ✓ 成功 {mut.data.succeeded}
          </span>
          <span
            className={`rounded px-3 py-1.5 ${
              mut.data.failed > 0 ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"
            }`}
          >
            ✗ 失败 {mut.data.failed}
          </span>
        </div>
        {failed.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-medium">失败明细</h3>
            <ul className="space-y-1 text-xs">
              {failed.map((r) => (
                <li key={r.task_id} className="font-mono">
                  <span className="text-muted-foreground">#{r.task_id}</span>{" "}
                  <span className="text-destructive">{r.error}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              失败 task 仍处于 scored 状态,可单独处理。
            </p>
          </div>
        )}
        <Button onClick={onDone}>完成</Button>
      </div>
    );
  }

  // Decision picker.
  if (!pendingDecision) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">批量决策</h2>
        <p className="text-sm text-muted-foreground">
          将对 <strong>{taskIds.length}</strong> 个评估任务应用同一决策。
          失败的任务保持 scored 状态可单独处理。
        </p>
        <div className="grid grid-cols-2 gap-2 max-w-md">
          <Button onClick={() => setPendingDecision("candidate")} variant="default">
            全部 accept (→ candidate)
          </Button>
          <Button onClick={() => setPendingDecision("rejected")} variant="outline">
            全部 reject
          </Button>
          <Button onClick={() => setPendingDecision("monitor_only")} variant="outline">
            全部 monitor_only
          </Button>
          <Button onClick={() => setPendingDecision("deferred")} variant="outline">
            全部 deferred (推迟)
          </Button>
        </div>
      </div>
    );
  }

  // Confirmation form.
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">确认批量决策</h2>
      <p className="text-sm">
        将对 <strong>{taskIds.length}</strong> 个任务应用决策{" "}
        <Badge variant={decisionVariant(pendingDecision)} className="ml-1">
          {pendingDecision}
        </Badge>
      </p>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">
          共享理由 (rationale,可选)
        </label>
        <Input
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder="所有 N 个任务共用此理由,留空也可"
          maxLength={500}
        />
      </div>
      {pendingDecision === "deferred" && (
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            下次评估时间 (deferred 必填)
          </label>
          <Input
            type="datetime-local"
            value={nextReview}
            onChange={(e) => setNextReview(e.target.value)}
          />
        </div>
      )}
      <div className="flex gap-2">
        <Button
          onClick={submit}
          disabled={
            mut.isPending || (pendingDecision === "deferred" && !nextReview)
          }
        >
          {mut.isPending ? "处理中…" : `确认提交 ${taskIds.length} 个`}
        </Button>
        <Button variant="ghost" onClick={() => setPendingDecision(null)}>
          返回选择决策
        </Button>
      </div>
      {mut.isError && (
        <p className="text-xs text-destructive">
          请求失败:{(mut.error as Error).message}
        </p>
      )}
    </div>
  );
}

function TaskListItem({
  task,
  active,
  onClick,
  checked,
  onCheck,
}: {
  task: EvaluationTask;
  active: boolean;
  onClick: () => void;
  checked: boolean;
  onCheck: () => void;
}) {
  const channelQ = useQuery({
    queryKey: ["channel", task.ChannelID],
    queryFn: () => endpoints.channel(task.ChannelID),
    staleTime: 60_000,
  });
  const channel = channelQ.data;
  return (
    <li
      className={cn(
        "border-b border-border transition-colors",
        active && !checked ? "bg-muted" : "",
        checked ? "bg-primary/5" : "hover:bg-muted/50",
      )}
    >
      <div className="flex items-start gap-2 px-3 py-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            e.stopPropagation();
            onCheck();
          }}
          onClick={(e) => e.stopPropagation()}
          className="mt-1 h-3.5 w-3.5 shrink-0"
        />
        <button onClick={onClick} className="flex-1 text-left">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate font-medium">
              {channel?.Name ?? channel?.Handle ?? `Channel #${task.ChannelID}`}
            </span>
            <Badge variant={taskStatusVariant(task.Status)}>{task.Status}</Badge>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            {channel && <PlatformBadge platform={channel.Platform} />}
            {channel?.FollowerCount && <span>· {fmtNumber(channel.FollowerCount)} fans</span>}
            <Badge variant={priorityVariant(task.Priority)} className="ml-auto">
              {task.Priority}
            </Badge>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {/* v2.4.3 — relative-age signal so the pending queue is
                age-stratifiable at a glance (date alone gave no signal of
                "how long has this been waiting"). */}
            创建 {fmtRelativeDays(task.CreatedAt)} · #{task.ID}
          </div>
        </button>
      </div>
    </li>
  );
}

function TaskDetailPanel({
  task,
  onDecided,
}: {
  task: EvaluationTask;
  onDecided: () => void;
}) {
  const channelQ = useQuery({
    queryKey: ["channel", task.ChannelID],
    queryFn: () => endpoints.channel(task.ChannelID),
  });
  const scoreQ = useQuery({
    queryKey: ["channel-score", task.ChannelID],
    queryFn: () => endpoints.channelScore(task.ChannelID),
    retry: (count, err) => {
      // 404 means "not yet computed" — don't retry.
      if (err instanceof APIError && err.status === 404) return false;
      return count < 2;
    },
  });

  const channel = channelQ.data;
  const score = scoreQ.data;
  const decided = task.Status === "decided";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">
              {channel?.Name ?? channel?.Handle ?? `Channel #${task.ChannelID}`}
            </h2>
            <p className="text-sm text-muted-foreground">
              {channel?.Url ? (
                <a
                  href={channel.Url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  {channel.Url}
                </a>
              ) : (
                channel?.PlatformChannelID
              )}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant={taskStatusVariant(task.Status)}>{taskStatusLabel(task.Status)}</Badge>
            {channel && (
              <Badge variant={lifecycleVariant(channel.LifecycleStatus)}>
                {lifecycleLabel(channel.LifecycleStatus)}
              </Badge>
            )}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
          <Field label="平台" value={channel && platformLabel(channel.Platform)} />
          <Field label="粉丝" value={fmtNumber(channel?.FollowerCount ?? null)} />
          <Field label="内容数" value={fmtNumber(channel?.TotalContentCount ?? null)} />
          <Field label="最后内容" value={fmtTimestamp(channel?.LastContentAt)} />
          <Field label="内容类型" value={channel?.ContentType ?? "—"} />
          <Field label="区域" value={channel?.Region ?? "—"} />
          <Field label="语言" value={channel?.Language ?? "—"} />
          <Field label="来源" value={task.Source} />
        </dl>
      </div>

      {/* Score panel */}
      <ScorePanel score={score} loading={scoreQ.isLoading} />

      {/* Decision form */}
      {decided ? (
        <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          此任务已决策，无法重复操作。频道当前状态：
          <Badge className="ml-2" variant={lifecycleVariant(channel?.LifecycleStatus ?? "")}>
            {channel?.LifecycleStatus ? lifecycleLabel(channel.LifecycleStatus) : "—"}
          </Badge>
        </div>
      ) : (
        <DecisionForm
          taskID={task.ID}
          suggestion={score?.AutoDecisionSuggestion ?? null}
          onDecided={onDecided}
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value ?? "—"}</dd>
    </div>
  );
}

function ScorePanel({ score, loading }: { score: ChannelScore | undefined; loading: boolean }) {
  if (loading) {
    return <p className="text-sm text-muted-foreground">分数加载中…</p>;
  }
  if (!score) {
    return (
      <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
        尚未计算分数（compute_scores 任务还没跑到这个频道）
      </div>
    );
  }

  const eng = numericToNumber(score.EngagementRate);
  const growth = numericToNumber(score.GrowthRate90d);
  const followerPct = numericToNumber(score.FollowerPct);
  const activityPct = numericToNumber(score.ActivityPct);
  const engPct = numericToNumber(score.EngagementPct);
  const growthPct = numericToNumber(score.GrowthPct);

  return (
    <div className="rounded-md border border-border bg-background p-4">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-semibold">评分快照</h3>
        <span className="text-xs text-muted-foreground">
          {fmtTimestamp(score.ComputedAt)}
        </span>
        {score.AutoDecisionSuggestion && (
          <Badge className="ml-auto" variant={decisionVariant(score.AutoDecisionSuggestion)}>
            自动建议：{score.AutoDecisionSuggestion}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <ScoreCell label="粉丝" value={fmtNumber(score.FollowerCount)} rank={fmtRank(followerPct)} />
        <ScoreCell label="30日活跃" value={fmtNumber(score.Activity30d)} rank={fmtRank(activityPct)} />
        <ScoreCell label="互动率 (30d)" value={fmtPercent(eng, 2)} rank={fmtRank(engPct)} />
        <ScoreCell label="90日增长" value={fmtPercent(growth, 1)} rank={fmtRank(growthPct)} />
        <ScoreCell label="竞品重叠 (90d)" value={fmtNumber(score.CompetitorOverlap)} rank="—" />
      </div>
    </div>
  );
}

function ScoreCell({
  label,
  value,
  rank,
}: {
  label: string;
  value: string;
  rank: string;
}) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{rank}</div>
    </div>
  );
}

function DecisionForm({
  taskID,
  suggestion,
  onDecided,
}: {
  taskID: number;
  suggestion: string | null;
  onDecided: () => void;
}) {
  const qc = useQueryClient();
  const [rationale, setRationale] = useState("");
  const [deferredDate, setDeferredDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateDecisionRequest) => endpoints.createDecision(body),
    onSuccess: () => {
      // Force-refresh anything that depends on this task / channel.
      qc.invalidateQueries({ queryKey: ["evaluation-tasks"] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      onDecided();
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(decision: DecisionKind) {
    setError(null);
    const body: CreateDecisionRequest = {
      task_id: taskID,
      decision,
      rationale: rationale.trim() || undefined,
    };
    if (decision === "deferred") {
      if (!deferredDate) {
        setError("延后决策需要选择下次复评日期");
        return;
      }
      // Push to end-of-day UTC so backend sees a future timestamp.
      const iso = new Date(`${deferredDate}T23:59:59Z`).toISOString();
      body.next_review_at = iso;
    }
    mutation.mutate(body);
  }

  const isSuggested = (d: DecisionKind) => {
    if (!suggestion) return false;
    // v2.13.x — the engine (internal/decision/engine.go:21-23) emits
    // "accept" | "reject" | "manual_review", written verbatim to
    // channel_scores.auto_decision_suggestion. This was checking
    // "auto_accept"/"auto_reject", which the engine NEVER writes, so no
    // button ever highlighted and the "高亮按钮 = 系统建议" hint was a
    // permanent lie. Map the real engine values to the operator's buttons.
    if (suggestion === "accept" && d === "candidate") return true;
    if (suggestion === "reject" && d === "rejected") return true;
    // "manual_review" intentionally highlights nothing — the engine is
    // saying "no confident auto-decision, review by hand".
    return false;
  };

  return (
    <div className="rounded-md border border-border bg-background p-4">
      <h3 className="mb-3 text-sm font-semibold">1-click 决策</h3>

      <label className="mb-1 block text-xs text-muted-foreground">决策说明（可选）</label>
      <textarea
        className="mb-3 h-20 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        placeholder="写下选择此决策的依据，会记入 audit_log"
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
      />

      <div className="mb-3 flex items-center gap-2">
        <label className="text-xs text-muted-foreground">延后复评日期</label>
        <Input
          type="date"
          value={deferredDate}
          onChange={(e) => setDeferredDate(e.target.value)}
          className="h-8 w-44"
        />
      </div>

      {error && <p className="mb-2 text-sm text-destructive">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => submit("candidate")}
          disabled={mutation.isPending}
          variant={isSuggested("candidate") ? "default" : "outline"}
        >
          通过为候选
        </Button>
        <Button
          onClick={() => submit("monitor_only")}
          disabled={mutation.isPending}
          variant={isSuggested("monitor_only") ? "default" : "outline"}
        >
          仅监控
        </Button>
        <Button
          onClick={() => submit("deferred")}
          disabled={mutation.isPending}
          variant={isSuggested("deferred") ? "default" : "outline"}
        >
          延后决策
        </Button>
        <Button
          onClick={() => submit("rejected")}
          disabled={mutation.isPending}
          variant={isSuggested("rejected") ? "destructive" : "outline"}
        >
          淘汰
        </Button>
        {suggestion && (
          <span className="ml-auto self-center text-xs text-muted-foreground">
            {suggestion === "manual_review"
              ? "系统建议：人工审核"
              : "高亮按钮 = 系统建议"}
          </span>
        )}
      </div>
    </div>
  );
}
